// ---------------------------------------------------------------------------
// 決算書5帳票 データ取得・整形エンジン (layout 駆動 / 2026-06-01 改修)
//   freee /api/1/reports/trial_bs|trial_pl を worker /api/freee-proxy 経由(GET読取のみ)で取得し、
//   financialLayout.ts の行順・群・小計定義に従って 5帳票(Excel テンプレ完全踏襲)を構築する。
//
//   設計方針(Excel 完全踏襲 + ロバスト化):
//   - account_item_display_type=account_item で取得 → leaf 勘定科目(account_item_name)を名前一致で充当。
//   - 明細(detail): layout の freeeAccountName を freee の account_item_name にマッチ → opening/debit/credit/closing。
//   - 小計(subtotal)/合計(grandtotal): layout の subtotalOf(行ラベル)を同帳票内で解決して合算。
//       PL の利益系(売上総利益/営業利益/経常利益/税引前/当期純利益)は PL_DERIVED_FORMULAS の符号付き式で算出。
//   - crossref(BS 当期純損益金額): PL 当期純利益を参照(同期間 PL を別途取得)。
//   - 構成比: 残高 / 分母(BS=負債・純資産合計 / PL=純売上高)×100。
//   - blank: 群間空行をそのまま行として保持(レイアウト踏襲)。
//
//   ロバスト化(新規/廃止勘定科目の自動追従):
//   - テンプレ未登録の freee 勘定科目(新規)は account_category_name から CATEGORY_TO_GROUP で
//     所属群を判定し、その群の小計直前に detail 行を自動挿入(isAutoInserted)。判定不能は
//     「その他(未分類)」群へ入れ meta.unmapped に記録(黙って落とさない・0埋めしない)。
//   - layout にあるが freee に無い科目は 0 表示(isZeroFilledRetired / meta.zeroFilledRetired)。
//   - 0埋め・推測補完で既知値を捏造しない(空応答は meta.empty で明示)。
// ---------------------------------------------------------------------------

import {
  FinancialReport, ReportColumn, ReportRow, FreeeTrialResponse, FreeeTrialBalanceRow,
  PeriodSelection, ReportKind, AutoInsertedAccount, UnmappedAccount,
  fiscalMonthToCalendar, lastDayOfMonth,
} from '../types/reports';
import {
  FINANCIAL_LAYOUTS, ReportLayout, LayoutRow, LayoutColumn,
  PL_DERIVED_FORMULAS, CATEGORY_TO_GROUP, UNMAPPED_GROUP_LABEL,
  SUB_BREAKDOWN_DYNAMIC, YOY_HEADER_GROUPS, COMPANY,
} from '../data/financialLayout';
import { adjustmentQuery, adjustNoteFor } from './adjustmentMode';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
const COMPANY_ID = COMPANY.companyId;
const HONBU_SECTION_ID = COMPANY.honbuSectionId; // 001 本部
const COMPANY_NAME = COMPANY.name;

// 整理仕訳: 画面の「整理仕訳 含む/除く」切替 (adjustmentMode.ts) に従い freee adjustment パラメータを付与。
//   既定は従来どおり「含む」(パラメータ無し)。注記は実際の取得条件と一致させる (adjustNoteFor)。

function jpDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${y}年${String(m).padStart(2, ' ')}月${String(d).padStart(2, ' ')}日`;
}

function printedNow(): string {
  const n = new Date();
  const hh = String(n.getHours()).padStart(2, '0');
  const mm = String(n.getMinutes()).padStart(2, '0');
  return `印刷日時 ${n.getFullYear()}年${String(n.getMonth() + 1).padStart(2, '0')}月${String(n.getDate()).padStart(2, '0')}日 ${hh}:${mm}`;
}

// =============================================================================
// 期間解決
// =============================================================================

export interface DateRange { start: string; end: string; }
export function resolveDateRange(p: PeriodSelection): {
  cur: DateRange; prev: DateRange; curMonthLabel: string;
} {
  const m = p.mode === 'range' ? (p.rangeEndMonth ?? p.fiscalMonth) : p.fiscalMonth;
  const startMonth = p.mode === 'range' ? (p.rangeStartMonth ?? 1) : m;
  const calStart = fiscalMonthToCalendar(p.fiscalTerm, startMonth);
  const calEnd = fiscalMonthToCalendar(p.fiscalTerm, m);
  const cur: DateRange = {
    start: `${calStart.year}-${String(calStart.month).padStart(2, '0')}-01`,
    end: lastDayOfMonth(calEnd.year, calEnd.month),
  };
  const pStart = fiscalMonthToCalendar(p.fiscalTerm - 1, startMonth);
  const pEnd = fiscalMonthToCalendar(p.fiscalTerm - 1, m);
  const prev: DateRange = {
    start: `${pStart.year}-${String(pStart.month).padStart(2, '0')}-01`,
    end: lastDayOfMonth(pEnd.year, pEnd.month),
  };
  const curMonthLabel = `${calEnd.year}年${calEnd.month}月`;
  return { cur, prev, curMonthLabel };
}

// =============================================================================
// freee 取得
// =============================================================================

async function fetchTrial(
  report: 'trial_bs' | 'trial_pl',
  params: Record<string, string | number>,
): Promise<FreeeTrialResponse> {
  const q = new URLSearchParams({ company_id: String(COMPANY_ID), ...adjustmentQuery() });
  for (const [k, v] of Object.entries(params)) q.set(k, String(v));
  const url = `${API_BASE}/api/freee-proxy/api/1/reports/${report}?${q.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    let body: any = null; try { body = await res.json(); } catch { /* */ }
    throw new Error(`freee ${report} failed: ${res.status} ${body?.message || ''}`);
  }
  return (await res.json()) as FreeeTrialResponse;
}

/** leaf 勘定科目(total_line でない行)だけを抽出 */
function leafBalances(resp: FreeeTrialResponse, report: 'trial_bs' | 'trial_pl'): FreeeTrialBalanceRow[] {
  const root = (resp as any)[report] || {};
  const balances: FreeeTrialBalanceRow[] = root.balances || [];
  return balances.filter((b) => !b.total_line && !!b.account_item_name);
}

// =============================================================================
// 値(勘定科目)モデル
// =============================================================================

/** 1 勘定科目の 4 値(BS/PL 共通) */
interface AcctValues { opening: number; debit: number; credit: number; closing: number; }
const ZERO: AcctValues = { opening: 0, debit: 0, credit: 0, closing: 0 };

function toAcct(b: FreeeTrialBalanceRow): AcctValues {
  return {
    opening: b.opening_balance ?? 0,
    debit: b.debit_amount ?? 0,
    credit: b.credit_amount ?? 0,
    closing: b.closing_balance ?? 0,
  };
}

/** 勘定科目名 → 値, 勘定科目名 → freee カテゴリー名 を作る */
function indexLeaves(leaves: FreeeTrialBalanceRow[]): {
  byName: Map<string, AcctValues>;
  catByName: Map<string, string>;
} {
  const byName = new Map<string, AcctValues>();
  const catByName = new Map<string, string>();
  for (const b of leaves) {
    const name = b.account_item_name as string;
    // 同名科目が複数(部門別)で来た場合は加算(本表は会社合計)
    const prev = byName.get(name);
    const v = toAcct(b);
    byName.set(name, prev
      ? { opening: prev.opening + v.opening, debit: prev.debit + v.debit, credit: prev.credit + v.credit, closing: prev.closing + v.closing }
      : v);
    if (b.account_category_name) catByName.set(name, b.account_category_name);
  }
  return { byName, catByName };
}

/**
 * group モードの total_line 行 → 決算書表示名(account_category_name) → AcctValues。
 * freee が特別集計する値(売上原価=期首+仕入-期末 / 特別損失カテゴリー集計 等)を
 * テンプレ再計算でなく freee の確定値で採用するために使う。単月取得時は
 * opening=前月末累計 / debit・credit=当月発生 / closing=当月末累計(YTD) で帳票列に対応。
 * 同名カテゴリーが階層違いで複数あった場合は最初(=最上位 hierarchy)を採用。
 */
function groupCategoryTotals(resp: FreeeTrialResponse, report: 'trial_bs' | 'trial_pl'): Map<string, AcctValues> {
  const root = (resp as any)[report] || {};
  const balances: FreeeTrialBalanceRow[] = root.balances || [];
  const m = new Map<string, AcctValues>();
  for (const b of balances) {
    if (!b.total_line) continue;
    const name = b.account_category_name;
    if (!name) continue;
    if (!m.has(name)) m.set(name, toAcct(b));
  }
  return m;
}

/**
 * group モードの total_line 行から「カテゴリー → 上位カテゴリー」(子→親)の対応を作る。
 *   freee の決算書カテゴリー階層(例: 他勘定振替高(商) → 商品売上原価 → 売上原価)を保持し、
 *   (a) CATEGORY_TO_GROUP 未登録の新規カテゴリーを祖先カテゴリー経由で正しい群へ落とす、
 *   (b) freeeCategory(売上原価/特別損失)集計に含まれる子孫カテゴリーを個別計上(二重計上)から除外する、
 *   の両方に使う。parent_account_category_name は freee 実レスポンスの total_line 行に格納される。
 */
function groupCategoryParents(resp: FreeeTrialResponse, report: 'trial_bs' | 'trial_pl'): Map<string, string> {
  const root = (resp as any)[report] || {};
  const balances: FreeeTrialBalanceRow[] = root.balances || [];
  const childToParent = new Map<string, string>();
  for (const b of balances) {
    if (!b.total_line) continue;
    const name = b.account_category_name;
    const parent = b.parent_account_category_name;
    if (name && parent && !childToParent.has(name)) childToParent.set(name, parent);
  }
  return childToParent;
}

async function fetchGroupData(
  report: 'trial_bs' | 'trial_pl', range: DateRange,
): Promise<{ totals: Map<string, AcctValues>; parents: Map<string, string> }> {
  const resp = await fetchTrial(report, { start_date: range.start, end_date: range.end, account_item_display_type: 'group' });
  return { totals: groupCategoryTotals(resp, report), parents: groupCategoryParents(resp, report) };
}

// =============================================================================
// layout 駆動 本表ビルダー(BS / PL / 前期PL)
// =============================================================================

const round2 = (v: number) => Math.round(v * 100) / 100;
const ratioOf = (v: number, denom: number) => (denom ? round2((v / denom) * 100) : 0);

/** subtotal/derived の解決順に必要なため、行ラベル→計算済 AcctValues を蓄積する作業マップ */
type ResolvedMap = Map<string, AcctValues>;

function sumAcct(parts: AcctValues[]): AcctValues {
  return parts.reduce((a, p) => ({
    opening: a.opening + p.opening, debit: a.debit + p.debit,
    credit: a.credit + p.credit, closing: a.closing + p.closing,
  }), { ...ZERO });
}

/** 符号付き合算(PL 派生小計用) */
function signedAcct(terms: { v: AcctValues; sign: 1 | -1 }[]): AcctValues {
  return terms.reduce((a, t) => ({
    opening: a.opening + t.sign * t.v.opening, debit: a.debit + t.sign * t.v.debit,
    credit: a.credit + t.sign * t.v.credit, closing: a.closing + t.sign * t.v.closing,
  }), { ...ZERO });
}

/**
 * 本表(BS/PL/前期PL)を layout 駆動で構築。
 *   - detail: freeeAccountName で値を引く(無ければ 0 表示=廃止科目)。
 *   - 新規 freee 科目: CATEGORY_TO_GROUP で群判定 → その群の小計直前に自動挿入。判定不能は その他(未分類)。
 *   - subtotal/grandtotal: subtotalOf を解決して合算。PL 利益系は PL_DERIVED_FORMULAS で符号付き算出。
 *   - crossref: 外部参照値(BS 当期純損益金額=PL 当期純利益)を注入。
 */
function buildTrialFromLayout(
  layout: ReportLayout,
  leaves: FreeeTrialBalanceRow[],
  crossRefValues: Map<string, AcctValues>, // sheet:label -> values (BS から PL を参照)
  groupTotals?: Map<string, AcctValues>, // freee 決算書表示名(group)→確定集計値。freeeCategory 行で採用
  hiddenLabels?: Set<string>, // 非表示ラベル: 計算(小計/合計/構成比)から除外 + 表示からも除外
  catParents?: Map<string, string>, // freee カテゴリー子→親。新規科目の祖先フォールバック/二重計上除外に使う
): {
  rows: ReportRow[];
  autoInserted: AutoInsertedAccount[];
  unmapped: UnmappedAccount[];
  zeroFilledRetired: string[];
} {
  const { byName, catByName } = indexLeaves(leaves);
  const reportKind: 'bs' | 'pl' = layout.kind === 'bs' ? 'bs' : 'pl';
  const isHidden = (label: string) => !!hiddenLabels && hiddenLabels.has(label);
  const hasHidden = !!hiddenLabels && hiddenLabels.size > 0;

  // 1) テンプレで明示列挙された勘定科目名の集合(これに無い freee 科目=新規)
  const templateAccountNames = new Set<string>();
  for (const r of layout.rows) {
    if (r.kind === 'detail' && r.freeeAccountName) templateAccountNames.add(r.freeeAccountName);
  }
  // freeeCategory(freee 集計値採用)行のうち「単一行 detail」(特別損失 等)が claim するカテゴリー。
  //   この種は配下を表示する明細枠が無く 1 行で集計値を出すので、配下 leaf は集計へ吸収(個別表示しない)。
  //   一方 freeeCategory を持つ「小計(subtotal)」(売上原価)は配下に明細枠があるので、新規 leaf は
  //   明細行として表示し、小計値は freee 集計値のまま(resolveSubtotal が newByGroup extra を無視=二重計上なし)。
  const claimedDetailCategories = new Set<string>();
  for (const r of layout.rows) if (r.freeeCategory && r.kind === 'detail') claimedDetailCategories.add(r.freeeCategory);

  // 2) 新規 freee 科目を群別に振り分け
  const categoryToGroup = new Map<string, string>();
  for (const m of CATEGORY_TO_GROUP) if (m.report === reportKind) categoryToGroup.set(m.category, m.group);

  // freee カテゴリー階層(子→親)を上に辿る。例: 他勘定振替高(商) → 商品売上原価 → 売上原価。
  const ancestorChain = (cat: string): string[] => {
    const chain: string[] = [];
    const seen = new Set<string>();
    let c: string | undefined = cat;
    while (c && catParents && !seen.has(c)) {
      seen.add(c);
      const p = catParents.get(c);
      if (!p) break;
      chain.push(p);
      c = p;
    }
    return chain;
  };
  // cat 自身 or 祖先が「単一行 freeeCategory(特別損失 等)」配下なら表示枠が無いので集計へ吸収(skip)。
  //   売上原価配下(他勘定振替高(商) 等)は吸収せず、下記 resolveGroup で売上原価群へ明細行として表示する。
  const isAbsorbedIntoDetailCategory = (cat: string): boolean =>
    claimedDetailCategories.has(cat) || ancestorChain(cat).some((a) => claimedDetailCategories.has(a));
  // 群解決: 明示 CATEGORY_TO_GROUP → freee 祖先カテゴリー経由フォールバック(新規カテゴリーも freee 登録分類に従い配置)
  const resolveGroup = (cat: string): string | undefined => {
    const direct = categoryToGroup.get(cat);
    if (direct) return direct;
    for (const a of ancestorChain(cat)) { const g = categoryToGroup.get(a); if (g) return g; }
    return undefined;
  };

  const autoInserted: AutoInsertedAccount[] = [];
  const unmapped: UnmappedAccount[] = [];
  // group(小計ラベル) -> 自動挿入する detail 行
  const newByGroup = new Map<string, { name: string; v: AcctValues }[]>();
  const unmappedRows: { name: string; v: AcctValues }[] = [];

  byName.forEach((v, name) => {
    if (templateAccountNames.has(name)) return; // テンプレ既知
    const cat = catByName.get(name) || '';
    if (isAbsorbedIntoDetailCategory(cat)) return; // 特別損失 等の単一行カテゴリ配下のみ吸収。売上原価配下(他勘定振替高(商))は明細行として表示する
    if (isHidden(name)) return; // 非表示の自動挿入科目は群合算・表示の双方から除外
    // 全ゼロ(休眠)科目はノイズになるので挿入しない
    if (v.opening === 0 && v.debit === 0 && v.credit === 0 && v.closing === 0) return;
    const group = resolveGroup(cat); // 明示マッピング → freee 祖先カテゴリー経由フォールバック
    const amount = reportKind === 'bs' ? v.closing : (v.closing - v.opening); // BS=残高 / PL=当月発生
    if (group) {
      if (!newByGroup.has(group)) newByGroup.set(group, []);
      newByGroup.get(group)!.push({ name, v });
      autoInserted.push({ accountName: name, categoryName: cat, group, amount });
    } else {
      unmappedRows.push({ name, v });
      unmapped.push({ accountName: name, categoryName: cat || '(カテゴリー不明)', amount });
    }
  });

  // 3) resolved マップ(行ラベル→値)。subtotal 解決に使う。
  const resolved: ResolvedMap = new Map();
  const zeroFilledRetired: string[] = [];

  // detail 値を先に確定(自動挿入分を含む群合算のため、群ごとの「明示+新規」リストを保持)
  // ラベル→値。同名ラベルが複数(BS の重複ラベル)あっても excelRow で区別されるが、
  // subtotalOf はラベル名で参照するため「最後に確定した detail 値」を使う。BS の重複ラベルは
  // 利益準備金(r110 明細 / r118 小計)等で、subtotalOf 側は小計ラベルを参照するので衝突しない。
  for (const r of layout.rows) {
    if (r.kind !== 'detail') continue;
    // 非表示 detail は ZERO 扱い(小計合算から除外)。表示も後段でスキップ。
    if (isHidden(r.label)) { resolved.set(r.label, { ...ZERO }); continue; }
    // freeeCategory 指定の detail(leaf が存在しないカテゴリー集計。例: 特別損失)は group 集計値を採用
    if (r.freeeCategory) {
      resolved.set(r.label, groupTotals?.get(r.freeeCategory) || { ...ZERO });
      continue;
    }
    if (r.freeeAccountName) {
      const v = byName.get(r.freeeAccountName);
      if (v) {
        resolved.set(r.label, v);
      } else {
        resolved.set(r.label, { ...ZERO });
        zeroFilledRetired.push(r.label);
      }
    }
  }
  // 自動挿入科目も resolved に(ラベル=科目名)
  newByGroup.forEach((list) => { for (const it of list) resolved.set(it.name, it.v); });
  for (const it of unmappedRows) resolved.set(it.name, it.v);

  // PL 派生小計の式索引
  const derivedByLabel = new Map(PL_DERIVED_FORMULAS.map((f) => [f.label, f]));

  // 4) subtotal/grandtotal を「上から順に」解決。subtotalOf が参照するラベルは
  //    その行より上で既に detail/subtotal として確定済みのものを使う。
  //    自動挿入分は所属群の subtotal に加算する。
  const resolveSubtotal = (r: LayoutRow): AcctValues => {
    // freee 集計値(売上原価=期末控除込み 等)を採用。ただし 非表示適用中は構成 leaf の除外を
    // 反映するため derived/subtotalOf 側(下記)で再集計する(derived==freeeCategory なので無害)。
    if (r.freeeCategory && groupTotals?.has(r.freeeCategory) && !(hasHidden && derivedByLabel.has(r.label))) {
      return groupTotals.get(r.freeeCategory)!;
    }
    // PL 派生(差引式)優先
    const derived = derivedByLabel.get(r.label);
    if (derived) {
      const terms = derived.terms.map((t) => ({ v: resolved.get(t.label) || { ...ZERO }, sign: t.sign }));
      return signedAcct(terms);
    }
    const parts: AcctValues[] = [];
    for (const lbl of r.subtotalOf || []) parts.push(resolved.get(lbl) || { ...ZERO });
    // この小計が「群の小計」なら、その群へ自動挿入した新規科目も合算
    const extra = newByGroup.get(r.label);
    if (extra) for (const it of extra) parts.push(it.v);
    return sumAcct(parts);
  };

  // 5) 出力行を組み立て(detail → 自動挿入 → subtotal → blank → ... を layout 順で)
  const out: ReportRow[] = [];
  const pushRow = (label: string, v: AcctValues | null, kind: ReportRow['kind'], indent: number, fill?: string, extra?: Partial<ReportRow>) => {
    const closing = v ? v.closing : null;
    const denom = 0; // 構成比は後で一括計算(分母確定後)
    out.push({
      label,
      values: v
        ? [v.opening, v.debit, v.credit, v.closing, denom]
        : [null, null, null, null, null],
      hierarchyLevel: kind === 'grandtotal' ? 1 : kind === 'subtotal' ? 2 : 3,
      isSubtotal: kind === 'subtotal',
      isTotal: kind === 'grandtotal',
      indent,
      kind,
      isBlank: kind === 'blank',
      ...extra,
    });
    void closing;
  };

  for (const r of layout.rows) {
    if (r.kind === 'blank') { out.push({ label: '', values: [null, null, null, null, null], hierarchyLevel: 3, isSubtotal: false, isTotal: false, indent: 0, kind: 'blank', isBlank: true }); continue; }
    if (r.kind === 'detail') {
      if (isHidden(r.label)) continue; // 非表示: 計算除外済(ZERO) + 表示スキップ
      const v = resolved.get(r.label) || { ...ZERO };
      const retired = zeroFilledRetired.includes(r.label);
      pushRow(r.label, v, 'detail', r.indent, r.fill, { isZeroFilledRetired: retired });
      continue;
    }
    if (r.kind === 'crossref') {
      const cr = r.crossRef!;
      const v = crossRefValues.get(`${cr.sheet}:${cr.label}`) || { ...ZERO };
      const stored = isHidden(r.label) ? { ...ZERO } : (resolved.get(r.label) || v);
      resolved.set(r.label, stored);
      if (isHidden(r.label)) continue; // 非表示 crossref: 親(株主資本計)から除外 + 表示スキップ
      pushRow(r.label, stored, 'subtotal', r.indent, r.fill);
      continue;
    }
    if (r.kind === 'subtotal' || r.kind === 'grandtotal') {
      const v = resolveSubtotal(r);
      // 非表示の小計は親への寄与を ZERO にし、表示・配下自動挿入もスキップ
      resolved.set(r.label, isHidden(r.label) ? { ...ZERO } : v);
      if (isHidden(r.label)) continue;
      // この小計の直前に、所属群の自動挿入新規科目を detail 行として差し込む(非表示は除外)
      const extra = newByGroup.get(r.label);
      if (extra) {
        for (const it of extra) {
          if (isHidden(it.name)) continue;
          out.push({
            label: it.name,
            values: [it.v.opening, it.v.debit, it.v.credit, it.v.closing, 0],
            hierarchyLevel: 3, isSubtotal: false, isTotal: false, indent: 1, kind: 'detail', isAutoInserted: true,
          });
        }
      }
      pushRow(r.label, v, r.kind, r.indent, r.fill);
      continue;
    }
  }

  // 6) その他(未分類)群を末尾(grandtotal 直前ではなく最後)に明示追加
  if (unmappedRows.length) {
    out.push({ label: '', values: [null, null, null, null, null], hierarchyLevel: 3, isSubtotal: false, isTotal: false, indent: 0, kind: 'blank', isBlank: true });
    for (const it of unmappedRows) {
      out.push({
        label: it.name,
        values: [it.v.opening, it.v.debit, it.v.credit, it.v.closing, 0],
        hierarchyLevel: 3, isSubtotal: false, isTotal: false, indent: 1, kind: 'detail', isAutoInserted: true,
      });
    }
    const unmappedSum = sumAcct(unmappedRows.map((it) => it.v));
    out.push({
      label: UNMAPPED_GROUP_LABEL,
      values: [unmappedSum.opening, unmappedSum.debit, unmappedSum.credit, unmappedSum.closing, 0],
      hierarchyLevel: 2, isSubtotal: true, isTotal: false, indent: 0, kind: 'subtotal', isAutoInserted: true,
    });
  }

  // 7) 構成比を一括計算(分母=ratioDenominatorLabel の残高 / closing)
  const denomLabel = layout.ratioDenominatorLabel;
  const denom = denomLabel ? (resolved.get(denomLabel)?.closing ?? 0) : 0;
  for (const row of out) {
    if (row.isBlank) continue;
    const closing = row.values[3];
    row.values[4] = closing == null ? null : ratioOf(closing, denom);
  }

  return { rows: out, autoInserted, unmapped, zeroFilledRetired };
}

function layoutColumns(cols: LayoutColumn[]): ReportColumn[] {
  return cols.map((c) => ({ key: c.key, label: c.label, fmt: c.fmt }));
}

// =============================================================================
// BS / PL / 前期PL
// =============================================================================

export async function fetchTrialBalanceReport(
  kind: 'bs' | 'pl' | 'pl_prev',
  period: PeriodSelection,
): Promise<FinancialReport> {
  const { cur, prev } = resolveDateRange(period);
  const range = kind === 'pl_prev' ? prev : cur;
  const report = kind === 'bs' ? 'trial_bs' : 'trial_pl';
  const layout = FINANCIAL_LAYOUTS[kind];

  // BS は当期純損益金額(=PL 当期純利益)を crossref する → 同期間 PL も取得
  const crossRefValues = new Map<string, AcctValues>();
  let resp: FreeeTrialResponse;
  let groupTotals: Map<string, AcctValues> | undefined; // 本表が freeeCategory 行を持つ場合に渡す
  let catParents: Map<string, string> | undefined; // freee カテゴリー子→親(新規科目の祖先フォールバック)
  if (kind === 'bs') {
    // BS 本体は freeeCategory 行なし(leaf 再計算)。crossref 用 PL は freeeCategory(売上原価/特別損失)を
    // 正しく解決するため PL の group 集計値も取得して PL build に渡す。BS 自身も group 階層を取得し
    // 新規 BS 科目の祖先カテゴリーフォールバックに使う。
    const [bsResp, plResp, plData, bsData] = await Promise.all([
      fetchTrial('trial_bs', { start_date: range.start, end_date: range.end, account_item_display_type: 'account_item' }),
      fetchTrial('trial_pl', { start_date: range.start, end_date: range.end, account_item_display_type: 'account_item' }),
      fetchGroupData('trial_pl', range),
      fetchGroupData('trial_bs', range),
    ]);
    resp = bsResp;
    // PL 当期純利益(売上原価=期末控除/特別損失=カテゴリー集計 込み)を crossref へ
    const plBuilt = buildTrialFromLayout(FINANCIAL_LAYOUTS.pl, leafBalances(plResp, 'trial_pl'), new Map(), plData.totals, undefined, plData.parents);
    const netIncome = plBuilt.rows.find((r) => r.label === '当期純利益');
    if (netIncome) {
      crossRefValues.set('pl:当期純利益', {
        opening: (netIncome.values[0] as number) ?? 0,
        debit: (netIncome.values[1] as number) ?? 0,
        credit: (netIncome.values[2] as number) ?? 0,
        closing: (netIncome.values[3] as number) ?? 0,
      });
    }
    catParents = bsData.parents;
  } else {
    // PL / 前期PL は freeeCategory(売上原価/特別損失)を持つので group 集計値・カテゴリー階層も取得
    const [r, g] = await Promise.all([
      fetchTrial(report, { start_date: range.start, end_date: range.end, account_item_display_type: 'account_item' }),
      fetchGroupData(report, range),
    ]);
    resp = r;
    groupTotals = g.totals;
    catParents = g.parents;
  }

  const leaves = leafBalances(resp, report);

  // 取得済みデータを closure で保持 → 非表示変更時は再fetchせず buildTrialFromLayout を再実行。
  const assemble = (hiddenLabels?: Set<string>): FinancialReport => {
    const built = buildTrialFromLayout(layout, leaves, crossRefValues, groupTotals, hiddenLabels, catParents);
    return {
      kind,
      landscape: layout.landscape,
      header: {
        title: layout.title,
        periodLabel: `自 ${jpDate(range.start)} 至 ${jpDate(range.end)}`,
        companyName: COMPANY_NAME,
        printedAt: printedNow(),
        taxNote: layout.notes.taxNote,
        unitNote: layout.notes.unitNote,
        adjustNote: adjustNoteFor(),
        sectionNote: layout.notes.sectionNote,
      },
      columns: layoutColumns(layout.columns),
      rows: built.rows,
      meta: {
        freee_display_type: 'account_item',
        start_date: range.start, end_date: range.end,
        up_to_date: resp.up_to_date ?? true,
        empty: leaves.length === 0,
        autoInserted: built.autoInserted.length ? built.autoInserted : undefined,
        unmapped: built.unmapped.length ? built.unmapped : undefined,
        zeroFilledRetired: built.zeroFilledRetired.length ? built.zeroFilledRetired : undefined,
      },
      rebuild: assemble,
    };
  };
  return assemble(undefined);
}

// =============================================================================
// 科目別前年同月対比表(会社合計) — layout(=PL 行構造) 駆動・13列
// =============================================================================

const yoyRound2 = (v: number) => Math.round(v * 100) / 100;
const yoyRatio = (v: number, denom: number) => (denom ? yoyRound2((v / denom) * 100) : 0);
const yoyDiffRate = (c: number, p: number) => (p ? yoyRound2((c / p) * 100) : 0);

export async function fetchYoyComparison(period: PeriodSelection): Promise<FinancialReport> {
  const { cur, prev } = resolveDateRange(period);
  const layout = FINANCIAL_LAYOUTS.yoy;
  const [cr, pr, curData, prevData] = await Promise.all([
    fetchTrial('trial_pl', { start_date: cur.start, end_date: cur.end, account_item_display_type: 'account_item' }),
    fetchTrial('trial_pl', { start_date: prev.start, end_date: prev.end, account_item_display_type: 'account_item' }),
    fetchGroupData('trial_pl', cur),
    fetchGroupData('trial_pl', prev),
  ]);
  const curLeaves = leafBalances(cr, 'trial_pl');
  const prevLeaves = leafBalances(pr, 'trial_pl');

  // ラベル→ row (発生=closing-opening / 残高=closing)
  const idx = (rows: ReportRow[]) => {
    const m = new Map<string, { hatsu: number; zan: number }>();
    for (const r of rows) {
      if (r.isBlank || !r.label) continue;
      const opening = (r.values[0] as number) ?? 0;
      const closing = (r.values[3] as number) ?? 0;
      m.set(r.label, { hatsu: closing - opening, zan: closing });
    }
    return m;
  };

  // 非表示変更時は curBuilt/prevBuilt を hidden 付きで再構築(小計が非表示を除外して再集計される)。
  const assemble = (hiddenLabels?: Set<string>): FinancialReport => {
    const curBuilt = buildTrialFromLayout(FINANCIAL_LAYOUTS.pl, curLeaves, new Map(), curData.totals, hiddenLabels, curData.parents);
    const prevBuilt = buildTrialFromLayout(FINANCIAL_LAYOUTS.pl, prevLeaves, new Map(), prevData.totals, hiddenLabels, prevData.parents);
    const curIdx = idx(curBuilt.rows);
    const prevIdx = idx(prevBuilt.rows);
    // 分母: 純売上高 の 発生 / 残高(非表示反映後)
    const curBase = curIdx.get('純売上高') || { hatsu: 0, zan: 0 };
    const prevBase = prevIdx.get('純売上高') || { hatsu: 0, zan: 0 };

    // 行順は curBuilt(=当期PLを layout駆動で構築。新規 freee 科目を売上原価等へ明細挿入済・非表示も適用済)に
    // 従う。これで本表 PL と同じ構造(他勘定振替高(商) 等の新規明細を含む)で当期/前期を対比できる。
    const rows: ReportRow[] = [];
    for (const rr of curBuilt.rows) {
      if (rr.isBlank) { rows.push({ label: '', values: Array(12).fill(null), hierarchyLevel: 3, isSubtotal: false, isTotal: false, indent: 0, kind: 'blank', isBlank: true }); continue; }
      const c = curIdx.get(rr.label) || { hatsu: 0, zan: 0 };
      const p = prevIdx.get(rr.label) || { hatsu: 0, zan: 0 };
      rows.push({
        label: rr.label,
        values: [
          c.hatsu, yoyRatio(c.hatsu, curBase.hatsu),
          p.hatsu, yoyRatio(p.hatsu, prevBase.hatsu),
          c.hatsu - p.hatsu, yoyDiffRate(c.hatsu, p.hatsu),
          c.zan, yoyRatio(c.zan, curBase.zan),
          p.zan, yoyRatio(p.zan, prevBase.zan),
          c.zan - p.zan, yoyDiffRate(c.zan, p.zan),
        ],
        hierarchyLevel: rr.hierarchyLevel,
        isSubtotal: rr.isSubtotal,
        isTotal: rr.isTotal,
        indent: rr.indent,
        kind: rr.kind,
        isAutoInserted: rr.isAutoInserted,
      });
    }

    return {
      kind: 'yoy',
      landscape: true,
      columnGroups: YOY_HEADER_GROUPS.map((g) => ({ label: g.label, span: g.span })),
      header: {
        title: layout.title,
        periodLabel: `自 ${jpDate(cur.start)} 至 ${jpDate(cur.end)}`,
        companyName: COMPANY_NAME, printedAt: printedNow(),
        taxNote: layout.notes.taxNote, unitNote: layout.notes.unitNote,
        adjustNote: adjustNoteFor(),
      },
      columns: layoutColumns(layout.columns),
      rows,
      meta: {
        freee_display_type: 'account_item',
        start_date: cur.start, end_date: cur.end, prev_start_date: prev.start, prev_end_date: prev.end,
        up_to_date: (cr.up_to_date ?? true) && (pr.up_to_date ?? true),
        empty: curLeaves.length === 0 && prevLeaves.length === 0,
        autoInserted: curBuilt.autoInserted.length ? curBuilt.autoInserted : undefined,
        unmapped: curBuilt.unmapped.length ? curBuilt.unmapped : undefined,
      },
      rebuild: assemble,
    };
  };
  return assemble(undefined);
}

// =============================================================================
// 補助科目内訳表(長期借入金・部門=本部) — items[] 動的展開
// =============================================================================

/** items[]/partners[] の1要素 → AcctValues */
function brkAcct(it: any): AcctValues {
  return {
    opening: it.opening_balance ?? 0, debit: it.debit_amount ?? 0,
    credit: it.credit_amount ?? 0, closing: it.closing_balance ?? 0,
  };
}
const isZeroAcct = (v: AcctValues) => v.opening === 0 && v.debit === 0 && v.credit === 0 && v.closing === 0;

/** trial_bs レスポンスから対象勘定科目(長期借入金)の balance 行を引く */
function findAccountRow(resp: FreeeTrialResponse, accountName: string): any | undefined {
  const bals = (resp.trial_bs?.balances || []) as any[];
  return bals.find((b) => b.account_item_name === accountName);
}

export async function fetchSubBreakdown(period: PeriodSelection): Promise<FinancialReport> {
  const { cur } = resolveDateRange(period);
  const layout = FINANCIAL_LAYOUTS.sub_breakdown;
  const acct = SUB_BREAKDOWN_DYNAMIC.parentAccountName; // 長期借入金
  const sec = HONBU_SECTION_ID;
  const base = { start_date: cur.start, end_date: cur.end, section_id: sec, account_item_display_type: 'account_item' as const };

  // 1) 品目(借入先契約)内訳。契約名・行順はこれを基準に保持。id=0=未選択(品目タグ漏れ)。
  const itemResp = await fetchTrial('trial_bs', { ...base, breakdown_display_type: 'item' });
  const itemParent = findAccountRow(itemResp, acct);
  const rawItems: any[] = (itemParent && Array.isArray(itemParent.items)) ? itemParent.items : [];

  // 契約(id!=0)行を順序保持で確定
  type LoanRow = { id: number; name: string; v: AcctValues };
  const loanRows: LoanRow[] = rawItems
    .filter((it) => (it.id ?? 0) !== 0)
    .map((it) => ({ id: it.id ?? -1, name: it.name || SUB_BREAKDOWN_DYNAMIC.emptyItemLabel, v: brkAcct(it) }));

  // 2) 未選択(品目タグ漏れ)があれば、取引先別に解決して正しい借入先へ統合。
  //    取引先タグは付いている(例: 有限会社蔵)ので、取引先別 品目内訳で各取引先の未選択分を特定し、
  //    同名(取引先名=品目名)の契約行へ加算。同名契約が無ければ取引先名の行を新規追加。推測せず freee 実データのみ。
  const untagged = rawItems.find((it) => (it.id ?? 0) === 0);
  if (untagged && !isZeroAcct(brkAcct(untagged))) {
    const partnerResp = await fetchTrial('trial_bs', { ...base, breakdown_display_type: 'partner' });
    const partnerParent = findAccountRow(partnerResp, acct);
    const partners: any[] = (partnerParent && Array.isArray(partnerParent.partners)) ? partnerParent.partners : [];
    for (const p of partners) {
      const ppResp = await fetchTrial('trial_bs', { ...base, breakdown_display_type: 'item', partner_id: p.id });
      const ppParent = findAccountRow(ppResp, acct);
      const ppItems: any[] = (ppParent && Array.isArray(ppParent.items)) ? ppParent.items : [];
      const ppUntagged = ppItems.find((it) => (it.id ?? 0) === 0);
      if (!ppUntagged) continue;
      const uv = brkAcct(ppUntagged);
      if (isZeroAcct(uv)) continue;
      // 同取引先名の契約行に統合(有限会社蔵 等)。無ければ取引先名で新規行。
      const target = loanRows.find((r) => r.name === p.name);
      if (target) {
        target.v = sumAcct([target.v, uv]);
      } else {
        loanRows.push({ id: -1, name: p.name || SUB_BREAKDOWN_DYNAMIC.emptyItemLabel, v: uv });
      }
    }
  }

  // 非表示変更時: 非表示の借入先行を除外し、合計を可視行のみで再集計(計算除外)。
  const assemble = (hiddenLabels?: Set<string>): FinancialReport => {
    const hid = (l: string) => !!hiddenLabels && hiddenLabels.has(l);
    const visible = loanRows.filter((r) => !hid(r.name));
    const rows: ReportRow[] = [];
    // 科目見出し(長期借入金)
    if (!hid(acct)) rows.push({ label: acct, values: [null, null, null, null], hierarchyLevel: 1, isSubtotal: false, isTotal: false, indent: 0, kind: 'detail' });
    for (const r of visible) {
      rows.push({
        label: r.name,
        values: [r.v.opening, r.v.debit, r.v.credit, r.v.closing],
        hierarchyLevel: 2, isSubtotal: false, isTotal: false, indent: SUB_BREAKDOWN_DYNAMIC.itemIndent, kind: 'detail',
      });
    }
    // 合計 = 可視の借入先行の合算(非表示は計算から除外)
    const tot = sumAcct(visible.map((r) => r.v));
    if (!hid('合計')) rows.push({
      label: '合計',
      values: [tot.opening, tot.debit, tot.credit, tot.closing],
      hierarchyLevel: 1, isSubtotal: false, isTotal: true, indent: 0, kind: 'grandtotal',
    });

    return {
      kind: 'sub_breakdown',
      landscape: layout.landscape,
      header: {
        title: layout.title,
        periodLabel: `自 ${jpDate(cur.start)} 至 ${jpDate(cur.end)}`,
        companyName: COMPANY_NAME, printedAt: printedNow(),
        taxNote: layout.notes.taxNote, unitNote: layout.notes.unitNote, sectionNote: layout.notes.sectionNote,
        adjustNote: adjustNoteFor(),
      },
      columns: layoutColumns(layout.columns),
      rows,
      meta: {
        freee_display_type: 'account_item', start_date: cur.start, end_date: cur.end,
        up_to_date: itemResp.up_to_date ?? true, empty: loanRows.length === 0,
      },
      rebuild: assemble,
    };
  };
  return assemble(undefined);
}

// =============================================================================
// ディスパッチ
// =============================================================================

export async function fetchReport(kind: ReportKind, period: PeriodSelection): Promise<FinancialReport> {
  if (kind === 'bs' || kind === 'pl' || kind === 'pl_prev') return fetchTrialBalanceReport(kind, period);
  if (kind === 'yoy') return fetchYoyComparison(period);
  if (kind === 'sub_breakdown') return fetchSubBreakdown(period);
  throw new Error(`unknown report kind ${kind}`);
}
